import {
  hasValue,
  normalizePhoneDigits,
} from '../components/PreviewCards/utils';

import type {
  PhoneContactAddress,
  PhoneContactEmail,
  PhoneContactNumber,
  PhoneFraudInfo,
  PhoneLineInfo,
  PhonePotentialOwner,
  PhoneReportData,
  PhoneSpamInfo,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toString(value: unknown): string {
  return value == null ? '' : String(value);
}

function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(lower)) {
      return true;
    }
    if (['false', '0', 'no', 'n'].includes(lower)) {
      return false;
    }
  }
  return Boolean(value);
}

function toNumberOrNull(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapPhoneLineNested(raw: unknown): PhoneLineInfo | null {
  const r = asRecord(raw);
  if (!r) {
    return null;
  }
  return {
    phoneNumber: toString(r.phone_number),
    carrier: toString(r.carrier),
    lineType: toString(r.line_type),
    userActivity: toString(r.user_activity),
    city: toString(r.city),
    state: r.state != null ? toString(r.state) : null,
    zip5: toString(r.zip5),
    timezone: toString(r.timezone),
    isValid: toBoolean(r.is_valid),
    isPrepaid: toBoolean(r.is_prepaid),
    isVoip: toBoolean(r.is_voip),
  };
}

/** `statusActivity` arrives as `"User Activity: none"` — extract the value after the colon. */
function mapPhoneLineFromRoot(
  r: Record<string, unknown>
): PhoneLineInfo | null {
  if (!r.phoneNumber && !r.carrier && !r.lineType) {
    return null;
  }
  const statusActivity = toString(r.statusActivity);
  const activityMatch = statusActivity.match(/user activity:\s*(.+)/i);
  const userActivity = activityMatch ? activityMatch[1].trim() : '';
  return {
    phoneNumber: toString(r.phoneNumber),
    carrier: toString(r.carrier),
    lineType: toString(r.lineType),
    userActivity,
    city: toString(r.location),
    state: null,
    zip5: toString(r.zipCode),
    timezone: toString(r.timeZone),
    isValid: toString(r.validNumber).toLowerCase() === 'yes',
    isPrepaid: toString(r.prepaid).toLowerCase() === 'yes',
    isVoip: toString(r.lineType).toLowerCase() === 'voip',
  };
}

function mapPotentialOwner(raw: unknown): PhonePotentialOwner | null {
  const r = asRecord(raw);
  if (!r || !r.name) {
    return null;
  }
  return {
    name: toString(r.name),
    bvId: toString(r.bvid),
    primaryAddress: toString(r.primary_address),
    primaryEmail: toString(r.primary_email),
  };
}

function mapSpam(raw: unknown): PhoneSpamInfo | null {
  const r = asRecord(raw);
  if (!r) {
    return null;
  }
  return {
    spamScore: Number(r.spam_score) || 0,
    nuisanceLevel: toString(r.nuisance_level),
    spamReportsCount: Number(r.spam_reports_count) || 0,
    numSearches: Number(r.num_searches) || 0,
    isSpammer: toBoolean(r.is_spammer),
  };
}

function mapFraud(raw: unknown): PhoneFraudInfo | null {
  const r = asRecord(raw);
  if (!r) {
    return null;
  }
  return {
    fraudScore: toNumberOrNull(r.fraud_score),
    riskLevel: toString(r.risk_level),
    isRisky: toBoolean(r.is_risky),
    isRecentAbuse: toBoolean(r.is_recent_abuse),
    isDoNotCall: toBoolean(r.is_do_not_call),
    isLeaked: toBoolean(r.is_leaked),
  };
}

/**
 * A phone report is "empty" when the number is not valid and there is nothing
 * else to show: no line details, owner, linked records, or real spam/fraud
 * signals. Default values (e.g. "Low" risk, "No Complaints Found", Prepaid/VoIP
 * "No") do not count as information. Used to suppress empty deepsearch previews.
 */
export function isPhoneReportEmpty(data: PhoneReportData): boolean {
  const line = data.phoneLine;
  const isValid = line?.isValid ?? false;

  const hasLineInfo = Boolean(
    line &&
      (hasValue(line.carrier) ||
        hasValue(line.lineType) ||
        hasValue(line.city) ||
        hasValue(line.state ?? '') ||
        hasValue(line.zip5) ||
        hasValue(line.timezone) ||
        hasValue(line.userActivity) ||
        line.isPrepaid ||
        line.isVoip)
  );

  const hasOwner =
    data.potentialOwner != null || data.otherPotentialOwners.length > 0;

  const hasLinkedRecords =
    data.potentialAddresses.length > 0 ||
    data.potentialEmails.length > 0 ||
    data.potentialPhoneNumbers.length > 0 ||
    data.jobs.length > 0 ||
    data.education.length > 0;

  const hasSpamSignal = Boolean(
    data.spam &&
      (data.spam.spamReportsCount > 0 ||
        data.spam.numSearches > 0 ||
        data.spam.isSpammer ||
        data.spam.spamScore > 0)
  );

  const hasFraudSignal = Boolean(
    data.fraud &&
      (data.fraud.isRisky ||
        data.fraud.isRecentAbuse ||
        data.fraud.isDoNotCall ||
        data.fraud.isLeaked ||
        (data.fraud.fraudScore ?? 0) > 0 ||
        ['medium', 'high'].includes(data.fraud.riskLevel.trim().toLowerCase()))
  );

  return (
    !isValid &&
    !hasLineInfo &&
    !hasOwner &&
    !hasLinkedRecords &&
    !hasSpamSignal &&
    !hasFraudSignal
  );
}

export function mapPhoneReportPayload(raw: unknown): PhoneReportData {
  const r = asRecord(raw) ?? {};

  const phoneLine = mapPhoneLineNested(r.phone_line) ?? mapPhoneLineFromRoot(r);

  const contactDetails =
    asRecord(r.contactDetails) ?? asRecord(r.contact_details) ?? {};

  const jobsEducation =
    asRecord(r.jobsEducation) ?? asRecord(r.jobs_education) ?? {};

  const potentialAddresses: PhoneContactAddress[] = asArray(
    contactDetails.potential_addresses
  ).map((a) => ({ full: toString(asRecord(a)?.full) }));

  const potentialEmails: PhoneContactEmail[] = asArray(
    contactDetails.potential_emails
  ).map((e) => ({ address: toString(asRecord(e)?.address) }));

  // The looked-up number is sometimes echoed back in potential_phone_numbers;
  // drop it (and any empty entries) so it isn't shown as an "other" number or
  // counted as a linked record.
  // phoneLine.phoneNumber can be an empty string, so fall back to the root
  // number rather than treating '' as a valid value.
  const currentDigits =
    normalizePhoneDigits(phoneLine?.phoneNumber ?? '') ||
    normalizePhoneDigits(toString(r.phoneNumber));

  const potentialPhoneNumbers: PhoneContactNumber[] = asArray(
    contactDetails.potential_phone_numbers
  )
    .map((p) => {
      const phone = asRecord(p);
      const type = toString(phone?.type);
      const lineType = toString(phone?.line_type ?? phone?.lineType);
      const resolvedType =
        lineType.length > 1
          ? lineType
          : type.length > 1
          ? type
          : lineType || type;

      return {
        number: toString(phone?.number),
        type: resolvedType,
      };
    })
    .filter((p) => {
      const digits = normalizePhoneDigits(p.number);
      return digits !== '' && digits !== currentDigits;
    });

  const potentialOwner =
    mapPotentialOwner(r.potentialOwner) ?? mapPotentialOwner(r.potential_owner);

  const otherPotentialOwners = asArray(
    r.otherPotentialOwners ?? r.other_potential_owners
  )
    .map(mapPotentialOwner)
    .filter((o): o is PhonePotentialOwner => o !== null);

  const permalink = toString(r.permalink);

  return {
    reportId: toString(r.reportId ?? r.report_id),
    phoneLine,
    potentialOwner,
    otherPotentialOwners,
    spam: mapSpam(r.spam),
    fraud: mapFraud(r.fraud),
    potentialAddresses,
    potentialEmails,
    potentialPhoneNumbers,
    jobs: asArray(jobsEducation.jobs).map(toString).filter(Boolean),
    education: asArray(jobsEducation.education).map(toString).filter(Boolean),
    ...(permalink ? { permalink } : {}),
  };
}
